// src/slices/ticketReturnAppraisal/reducer.ts
// Redux slice cho US-018 — Thẩm định & phê duyệt hồ sơ thu hồi vé ế

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  RECONCILIATION_STATUS_QUERY,
  TicketReturnReconciliation,
} from 'slices/ticketReturnReconciliation/types';
import {
  TicketReturnAppraisalFilters,
  TicketReturnAppraisalModalState,
  TicketReturnAppraisalState,
} from './types';
import {
  fetchAppraisalReconciliations,
  fetchAppraisalReconciliationById,
  appraiseReconciliation,
} from './thunk';

const initialFilters: TicketReturnAppraisalFilters = {
  // Mặc định chỉ hiện phiếu đang chờ thẩm định (đúng yêu cầu US-018)
  status: RECONCILIATION_STATUS_QUERY.ChoThamDinh,
  agentId: '',
};

const initialState: TicketReturnAppraisalState = {
  list: [],
  selectedItem: null,
  pagination: {
    currentPage: 1,
    pageSize: 20,
    totalItems: 0,
    totalPages: 0,
  },
  filters: { ...initialFilters },
  loading: false,
  loadingDetail: false,
  submitting: false,
  error: null,
  modalState: {
    detail: false,
    appraise: false,
  },
};

const ticketReturnAppraisalSlice = createSlice({
  name: 'TicketReturnAppraisal',
  initialState,
  reducers: {
    setSelectedItem: (state, action: PayloadAction<TicketReturnReconciliation | null>) => {
      state.selectedItem = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageSize = action.payload;
      state.pagination.currentPage = 1;
    },
    setFilters: (state, action: PayloadAction<Partial<TicketReturnAppraisalFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = { ...initialFilters };
      state.pagination.currentPage = 1;
    },
    clearError: (state) => {
      state.error = null;
    },
    openModal: (state, action: PayloadAction<keyof TicketReturnAppraisalModalState>) => {
      state.modalState[action.payload] = true;
    },
    closeModal: (state, action: PayloadAction<keyof TicketReturnAppraisalModalState>) => {
      state.modalState[action.payload] = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Danh sách phiếu
      .addCase(fetchAppraisalReconciliations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAppraisalReconciliations.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        state.list = payload?.items || payload?.data || payload || [];
        state.pagination.totalItems = payload?.totalItems ?? payload?.totalCount ?? state.list.length;
        state.pagination.totalPages =
          payload?.totalPages ??
          Math.ceil(state.pagination.totalItems / Math.max(state.pagination.pageSize, 1));
      })
      .addCase(fetchAppraisalReconciliations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Chi tiết phiếu (kèm attachmentUrl signed sẵn)
      .addCase(fetchAppraisalReconciliationById.pending, (state) => {
        state.loadingDetail = true;
      })
      .addCase(fetchAppraisalReconciliationById.fulfilled, (state, action) => {
        state.loadingDetail = false;
        state.selectedItem = action.payload as TicketReturnReconciliation;
      })
      .addCase(fetchAppraisalReconciliationById.rejected, (state, action) => {
        state.loadingDetail = false;
        state.error = action.payload as string;
      })

      // Ghi kết quả thẩm định — API chỉ trả id nên không thể cập nhật item tại chỗ,
      // nơi gọi phải refetch danh sách.
      .addCase(appraiseReconciliation.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(appraiseReconciliation.fulfilled, (state) => {
        state.submitting = false;
        state.modalState.appraise = false;
        state.modalState.detail = false;
        state.selectedItem = null;
      })
      .addCase(appraiseReconciliation.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setSelectedItem,
  setPage,
  setPageSize,
  setFilters,
  clearFilters,
  clearError,
  openModal,
  closeModal,
} = ticketReturnAppraisalSlice.actions;

export default ticketReturnAppraisalSlice.reducer;
